import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ReadableValue, readableLabel } from './DataIntroductionProposal';

const FIELD_LABELS = {
    unit_count: 'Number of units', blocks_units: 'Blocks and units',
    gia: 'Gross internal area', gea: 'Gross external area', nia: 'Net internal area',
    gross_internal_area: 'Gross internal area', measurement_basis: 'Measurement basis',
    intake: 'Additional project data', email_detail: 'Email detail'
};
const SOURCE_LABELS = {
    email: 'Email search', gmail: 'Email search', email_search: 'Email search',
    soi: 'Schedule of Information', soi_upload: 'Schedule of Information',
    soi_mapping: 'Schedule of Information', data_pack: 'Data Pack',
    questionnaire: 'Questionnaire', initial_questionnaire: 'Initial questionnaire',
    interim_questionnaire: 'Interim questionnaire', portal: 'Project record',
    notes: 'Site notes', notes_upload: 'Uploaded site notes',
    voice: 'Voice entry', voice_transcription: 'Voice transcript',
    ai_review: 'AI Review', surveyor_review: 'Surveyor review'
};

export function fieldLabel(value) {
    return FIELD_LABELS[value] || readableLabel(String(value || '')
        .replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' '));
}

export function sourceLabel(value) {
    return value ? SOURCE_LABELS[value] || fieldLabel(value) : 'Source';
}

export function conflictTitle(conflict, issues = []) {
    if (conflict.kind === 'source_processing') {
        const runId = String(conflict.field_key || '').split('.').pop();
        const issue = issues.find(item => String(item.run_id) === runId);
        const type = issue?.intake_type || conflict.label?.match(/^Unprocessed (.+) source \d+$/i)?.[1];
        return `${sourceLabel(type)} needs processing`;
    }
    const field = String(conflict.field_key || '');
    const generated = field.replace(/[._]/g, ' ');
    if (conflict.label && conflict.label.toLowerCase() !== generated.toLowerCase()
        && conflict.label !== field) return conflict.label;
    return fieldLabel(field.split('.').pop()) || 'Report detail';
}

export function conflictContext(conflict) {
    if (conflict.kind === 'source_processing') return '';
    const sections = String(conflict.field_key || '').split('.').slice(0, -1).map(fieldLabel);
    const entity = String(conflict.entity_id || '');
    if (entity && entity !== 'site' && !entity.includes(':')
        && !/^[a-f0-9]{8}-[a-f0-9-]{27,}$/i.test(entity)) sections.push(fieldLabel(entity));
    return sections.join(' / ');
}

export function conflictExplanation(conflict) {
    if (conflict.kind === 'source_processing') return '';
    return {
        incomparable: 'These entries use different units, measurement bases, currencies or VAT assumptions. Check the evidence before choosing the report value.',
        contradictory_values: 'The sources give different values. Choose the value to use in this report.',
        uncertain_extraction: 'This detail could not be confirmed during extraction. Check the evidence, correct the value or mark it unknown.'
    }[conflict.reason] || (conflict.reason ? fieldLabel(conflict.reason) : '');
}

export function processingMessage(issue) {
    if (issue.code !== 'INTAKE_PROCESSING_REQUIRED') return issue.message;
    const name = sourceLabel(issue.intake_type);
    return issue.status === 'processing'
        ? `${name} is still being processed. Wait for it to finish, then retry the check.`
        : `${name} needs complete field extraction before it can be included in this report.`;
}

function readLocator(value) {
    if (!value) return {};
    if (typeof value === 'object' && !Array.isArray(value)) return value;
    if (typeof value === 'string' && /^[{[]/.test(value.trim())) {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
        } catch {
            return { unreadable: true };
        }
        return { unreadable: true };
    }
    return typeof value === 'string' ? { location: value } : { unreadable: true };
}

function supplied(value) {
    return value !== undefined && value !== null && value !== '';
}

function sourceDate(value) {
    const text = String(value);
    const date = new Date(text);
    return /^\d{4}-\d{2}-\d{2}/.test(text) && Number.isFinite(date.getTime())
        ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
        : text;
}

export function ReportDataSource({ source }) {
    const locator = readLocator(source.locator);
    const evidence = Array.isArray(locator.evidence) ? locator.evidence.filter(item => item && typeof item === 'object') : [];
    const entries = [source, ...evidence];
    return <Stack spacing={1.5}>
        {entries.map((entry, index) => {
            const location = index ? { ...locator, ...readLocator(entry.locator), ...entry } : locator;
            const metadata = { ...location, ...entry };
            const date = metadata.effective_at || metadata.received_at;
            const file = metadata.filename || metadata.file_name || metadata.source_file || metadata.source;
            const excerpt = [
                index ? entry.exact_excerpt ?? entry.reference_text ?? entry.excerpt : locator.exact_excerpt,
                entry.source_excerpt, entry.excerpt, location.excerpt,
                typeof entry.raw_value !== 'object' ? entry.raw_value : undefined
            ].find(supplied);
            const details = [
                ['File', file], ['Subject', metadata.subject || metadata.email_subject],
                ['From', metadata.sender], ['Date', date ? sourceDate(date) : null],
                ['Location', location.location], ['Sheet', location.sheet],
                ['Cell', location.cell || location.response_cell], ['Page', location.page || location.page_number],
                ['Section', location.section], ['Time in recording', location.timecode]
            ].filter(([, value]) => supplied(value) && typeof value !== 'object');
            return <Box key={index}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{sourceLabel(entry.source_channel || source.source_channel)}</Typography>
                {details.map(([label, value]) => <Typography key={label} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{label}: </Box>{String(value)}
                </Typography>)}
                {location.unreadable && <Typography variant="body2" color="text.secondary">The source location could not be read.</Typography>}
                {supplied(excerpt) ? <Box component="blockquote" sx={{ m: 0, mt: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Reference text</Typography>
                    <ReadableValue value={excerpt} />
                </Box> : !details.length && <Typography variant="body2" color="text.secondary">No reference text or source location was supplied.</Typography>}
            </Box>;
        })}
    </Stack>;
}
